import apiClient, { fetchWithSessionRefresh } from "./index";
import API_BASE_URL from "./API_BASE_URL";
import { csrfHeaders } from "../utils/csrf";

export interface UserSignatureReference {
  type: "user_signature";
  id: number;
}

export interface UserSignatureInfo {
  id: number;
  name: string;
  originalFileName: string;
  originalMimeType: string;
  processedMimeType: string;
  originalFileSize: number;
  processedFileSize: number;
  originalWidth: number;
  originalHeight: number;
  processedWidth: number;
  processedHeight: number;
  inkCoverage: number;
  processorVersion: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProcessedSignaturePreview {
  processedImageBase64: string;
  processedMimeType: string;
  originalWidth: number;
  originalHeight: number;
  processedWidth: number;
  processedHeight: number;
  processedFileSize: number;
  inkCoverage: number;
  processorVersion: string;
}

type ApiEnvelope<T> = {
  success: boolean;
  data: T;
  message?: string;
  code?: string;
};

const base = "/users/signatures";

/**
 * Task 149/150. There is no bearer token to attach any more — the HttpOnly
 * session cookie authenticates these, and `credentials: "include"` on the
 * request is what sends it. These are multipart writes, so what they do need
 * is the CSRF token.
 */
function authHeaders(): Record<string, string> {
  return csrfHeaders();
}

async function multipartRequest<T>(
  path: string,
  file: File,
  fields: Record<string, string> = {}
): Promise<T> {
  const formData = new FormData();
  formData.append("signature", file);
  Object.entries(fields).forEach(([key, value]) => formData.append(key, value));

  // Built per attempt so a retry after a session refresh carries the new
  // CSRF token.
  const response = await fetchWithSessionRefresh(`${API_BASE_URL}${path}`, () => ({
    method: "POST",
    headers: authHeaders(),
    body: formData,
    credentials: "include",
  }));
  const payload = (await response.json().catch(() => ({}))) as Partial<ApiEnvelope<T>>;
  if (!response.ok) {
    throw new Error(payload.message || `Signature request failed (${response.status})`);
  }
  if (!payload.data) throw new Error("Signature service returned an invalid response");
  return payload.data;
}

export async function listUserSignatures(): Promise<UserSignatureInfo[]> {
  const response = await apiClient.get<ApiEnvelope<{ items: UserSignatureInfo[] }>>(base);
  return response.data.data?.items ?? [];
}

export function processUserSignaturePreview(file: File): Promise<ProcessedSignaturePreview> {
  return multipartRequest<ProcessedSignaturePreview>(`${base}/preview-process`, file);
}

export function createUserSignature(
  file: File,
  name: string
): Promise<UserSignatureInfo> {
  return multipartRequest<UserSignatureInfo>(base, file, {
    name,
    approved: "true",
  });
}

export async function renameUserSignature(
  id: number,
  name: string
): Promise<UserSignatureInfo> {
  const response = await apiClient.patch<ApiEnvelope<UserSignatureInfo>>(`${base}/${id}`, {
    name,
  });
  return response.data.data;
}

export async function getUserSignaturePreviewUrl(
  id: number,
  variant: "processed" | "original" = "processed"
): Promise<string> {
  const response = await apiClient.get<ApiEnvelope<{ url: string }>>(
    `${base}/${id}/preview`,
    { params: { variant } }
  );
  return response.data.data?.url ?? "";
}

export async function deleteUserSignature(id: number): Promise<void> {
  await apiClient.delete(`${base}/${id}`);
}
